import { FileSearchOutlined, FileTextOutlined } from '@ant-design/icons';
import type { ResponseGetReports } from '@vindr/services/apis/report.api';
import { GENDER, LANGUAGES } from '@vindr/services/constants/common.constant';
import { REPORT_VARIABLES } from '@vindr/services/constants/report.constant';
import { autoSpace, convertPhoneNumber } from '@vindr/utils/formatText';
import {
  generateShareLink,
  generateVisitLink,
  getAccessKeys,
} from '@vindr/utils/report/generateLink';
import { Button, Dropdown, Modal, Spin } from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import QRCode from 'qrcode';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { DraggableData, DraggableEvent } from 'react-draggable';
import Draggable from 'react-draggable';
import { useTranslation } from 'react-i18next';
import { ResizableBox } from 'react-resizable';
import './ReportViewport.css';
import { getDateFormatted, groupByKey } from '@vindr/utils/helpers/common.helper';
import StructuredElement from '../StructuredReport/components/elements/structured/StructuredElement';
import { CaretDownOutlined } from '@vindr/icons';
import { formatDateColumn } from '@vindr/utils/dateTime';
import { ESIGN_STATUS } from '@vindr/services/apis/esign.api';
import { blobToURL } from '@vindr/utils/converter';
import PDFViewer from '../../ui/PDFViewer';
import {
  ParamsDownloadImageElement,
  ResponseDownloadImageElement,
  setImageSrcToStructure,
} from '@vindr/services/apis/element.api';

const INIT_REPORT_Z_INDEX = 600;
function ReportViewport({
  userAuthenticationService,
  httpClientService,
  modalWidth = 680,
  isActive = true,
  study,
}: {
  userAuthenticationService: any;
  httpClientService: any;
  modalWidth: number;
  isActive: boolean;
  study: any;
}) {
  const { t } = useTranslation('Vindoc');
  const [isOpen, setIsOpen] = useState(false);
  const btnRef = useRef<HTMLElement>();
  const btnClientRect: any = btnRef?.current?.getBoundingClientRect() || {};

  return (
    <div>
      <Button
        ref={btnRef}
        className="flex items-center justify-center"
        icon={<FileTextOutlined className="m-auto" />}
        size="small"
        title={t('Report')}
        type="text"
        onClick={() => {
          setIsOpen(true);
        }}
      />
      {isOpen && (
        <ReportModal
          httpClientService={httpClientService}
          isActive={isActive}
          isOpen={isOpen}
          modalWidth={modalWidth}
          study={study}
          userAuthenticationService={userAuthenticationService}
          position={{
            top: `${(btnClientRect.bottom || 20) + 12}px`,
            left: `${(btnClientRect.right || modalWidth) - modalWidth}px`,
          }}
          onCancel={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}

ReportViewport.propTypes = {
  userAuthenticationService: PropTypes.any.isRequired,
  modalWidth: PropTypes.number,
  isActive: PropTypes.bool,
  study: PropTypes.object,
};

export default React.memo(ReportViewport);

function ReportModal({
  userAuthenticationService,
  httpClientService,
  isOpen,
  onCancel,
  position = {},
  modalWidth,
  isActive,
  study,
}) {
  const { t, i18n } = useTranslation('Vindoc');
  const DATE_FORMAT = i18n.language === LANGUAGES.VI ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
  const DATE_TIME_FORMAT = `${DATE_FORMAT} HH:mm:ss`;

  const [disabled, setDisabled] = useState(true);
  const [isResizing, setIsResizing] = useState(false);
  const [isFetchingReport, setIsFetchingReport] = useState(true);
  const [reportList, setReportList] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [bounds, setBounds] = useState({
    left: 0,
    top: 0,
    bottom: 0,
    right: 0,
  });
  const [resizeDimension, setResizeDimension] = useState({
    width: modalWidth,
    height: 500,
  });
  const [dragPosition, setDragPosition] = useState({
    x: 0,
    y: 0,
  });

  const draggleRef = useRef<HTMLDivElement>(null);
  let modalZIndex = INIT_REPORT_Z_INDEX;
  const { studyInstanceUid, date: studyDateTime, description, modalities } = study || {};

  const studyInfo = useMemo(() => {
    const arrStudyInfo = [];
    if (description && description !== '*') {
      arrStudyInfo.push(description);
    }
    if (studyDateTime) {
      arrStudyInfo.push(studyDateTime);
    }
    return arrStudyInfo.join(' | ');
  }, [description, studyDateTime]);

  useEffect(() => {
    handleFetchReport(studyInstanceUid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyInstanceUid]);

  if (isActive) {
    const numOpenReportModal = document.querySelectorAll('.report-draggable-modal') || [];
    modalZIndex = INIT_REPORT_Z_INDEX + numOpenReportModal.length;
  } else {
    modalZIndex = INIT_REPORT_Z_INDEX;
  }

  // const handleFetchInstitution = async (risBaseUrl, institutionId) => {
  //   const resInstitutions = await fetch(`${risBaseUrl}/institutions/v1/${institutionId}`, {
  //     headers: userAuthenticationService?.getAuthorizationHeader(),
  //   });
  //   const { data } = await resInstitutions.json();
  //   return data;
  // };

  // const handleFetchOrder = async (risBaseUrl, orderId) => {
  //   const resInstitutions = await fetch(`${risBaseUrl}/orders/v1/${orderId}`, {
  //     headers: userAuthenticationService?.getAuthorizationHeader(),
  //   });
  //   const { data } = await resInstitutions.json();
  //   return data;
  // };

  const handleFetchEsignReport = async (risBaseUrl, esignId) => {
    const authHeaders = userAuthenticationService?.getAuthorizationHeader();
    try {
      const resApprover = await fetch(
        `${risBaseUrl.replace('/api/ris', '/api/esign')}${
          authHeaders?.Authorization ? '/v1' : ''
        }/esigns/${esignId}`,
        { headers: authHeaders }
      );
      const { data } = await resApprover.json();
      return data;
    } catch (err) {
      return {};
    }
  };

  const handleFetchVersionDetail = async (risBaseUrl, versionId) => {
    try {
      const resApprover = await fetch(`${risBaseUrl}/reports/versions/${versionId}`, {
        headers: userAuthenticationService?.getAuthorizationHeader(),
      });
      const { data } = await resApprover.json();
      return data;
    } catch (err) {
      return {};
    }
  };

  const getImageUrl = async (body: ParamsDownloadImageElement) => {
    try {
      const response = await fetch(`${window?.config?.risURL}/file-storage/v1/request-download`, {
        headers: {
          ...userAuthenticationService?.getAuthorizationHeader(),
          'content-type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify(body),
      });
      return (await response.json()) as ResponseDownloadImageElement;
    } catch (err) {
      return;
    }
  };

  const handleFetchReport = async (studyInstanceUid) => {
    try {
      const risBaseUrl = window?.config?.risURL;
      if (!risBaseUrl) return;
      setIsFetchingReport(true);
      const response = (await httpClientService.getReport(studyInstanceUid)) as ResponseGetReports;
      const { data = [] } = response;

      let latestReports;
      if (studyInstanceUid.startsWith('STUDY') && data?.length > 1) {
        // Fake study_instance_uid so we should get only the last report
        latestReports = data.sort(
          (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        )[0];
      } else {
        latestReports = data;
      }
      if (!latestReports) {
        setIsFetchingReport(false);
        return;
      }

      for (const latestReport of latestReports) {
        const versionId = latestReport.report_version.id;

        if (!versionId) {
          setIsFetchingReport(false);
          continue;
        }

        const versionDetail = await handleFetchVersionDetail(window?.config?.risURL, versionId);
        // const orderDetail = await handleFetchOrder(window?.config?.risURL, latestReport.order_id);
        latestReport.report_version = versionDetail;
        // latestReport.order = orderDetail;
        latestReport.approver = versionDetail.approver;
        if (
          [
            ESIGN_STATUS['Approver Signed'],
            ESIGN_STATUS['Submitter Signed'],
            ESIGN_STATUS['Approver Failed'],
            ESIGN_STATUS['Revoke Failed'],
          ].includes(versionDetail?.esign_order?.status)
        ) {
          const esignDetail = await handleFetchEsignReport(
            window?.config?.risURL,
            versionDetail?.esign_order?.id
          );
          const signed_report = esignDetail.signed_report;
          if (!signed_report) {
            const response = await fetch(signed_report);
            if (!response.ok) throw new Error('Network response was not ok');
            const blob = await response.blob();
            latestReport.pdfURL = blobToURL(blob);
            setIsFetchingReport(false);
            continue;
          }
        }

        // const institutionDetail = await handleFetchInstitution(
        //   window?.config?.risURL,
        //   latestReport.institution_id
        // );

        // latestReport.institution = institutionDetail;

        // latestReport.approver = approver;
        // const institutionId = latestReport?.institution_id;
        // const reportId = latestReport?.id;

        if (latestReport.structure) {
          latestReport.structure.data = await setImageSrcToStructure(
            latestReport.structure.data,
            getImageUrl
          ).catch((error) => (console.error(error), latestReport.structure.data));
        }

        const { study_access_key, visit_access_key } =
          getAccessKeys(latestReport.share_access_keys) || {};
        const shareUrl = generateShareLink(
          study_access_key?.study_instance_uid || studyInstanceUid,
          study_access_key?.key,
          modalities
        );
        const shareVisit = generateVisitLink({
          visit_id: visit_access_key?.visit_id,
          access_key: visit_access_key?.key,
        });
        setIsFetchingReport(false);

        const qrRes = (await QRCode.toDataURL(shareUrl).catch(console.error)) || '';
        const QRShareVisit = (await QRCode.toDataURL(shareVisit).catch(console.error)) || '';

        latestReport.transformedContent = transformReportContent(
          latestReport || ({} as any),
          qrRes,
          QRShareVisit
        );
      }
      setReportList(latestReports);
      setSelectedReport(latestReports[0]);
    } catch (error) {
      setIsFetchingReport(false);
    } finally {
      setIsFetchingReport(false);
    }
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const transformReportContent = (
    report: ResponseGetReports['data'][number],
    qrBase64: string,
    QRShareVisit: string
  ) => {
    const {
      report_version,
      updated_at,
      institution,
      order,
      share_access_keys,
      is_structure_report,
    } = report;
    const { visited_patient, procedure } = order;

    const {
      approver,
      conclusion: ReportConclusion,
      content: ReportContent,
      layout,
      submitter,
      technician,
    } = report_version;

    const { signature, title, fullname } = approver ?? {};
    const approverName = autoSpace(title || '', fullname || '');
    const { title: smTitle, fullname: smName } = submitter ?? {};
    const submitterName = `${smTitle ? smTitle + ' ' : ''}${smName || ''}`;
    const layoutContent = layout?.content || ReportContent || '';
    const { visit_access_key } = getAccessKeys(share_access_keys);

    const structureReportData = is_structure_report
      ? groupByKey(
          (report?.structure?.data || []).map(
            (it) =>
              new StructuredElement({
                ...it,
                value: it.value || it.content,
                elements: it.elements,
                id: it.id,
                name: it.name,
                type: it.type,
              })
          ),
          'name'
        )
      : {};

    const {
      logo: InstitutionLogo,
      name: InstitutionName,
      address: InstitutionAddress,
      phone: InstitutionPhone,
      email: InstitutionEmail,
    } = institution ?? {};

    const {
      order_doctor_name: OrderDoctorName,
      refer_doctor_name: ReferDoctorName,
      diagnostic: Diagnosis,
      scheduled_date,
      order_date: OrderDate,
    } = order ?? {};

    const {
      patient_id: PatientID,
      visit_id: VisitID,
      name: PatientName,
      birth_date: PatientBirthday,
      sex: PatientSex,
      address: PatientAddress,
      phone: PatientPhone,
      birth_date,
    } = visited_patient ?? {};

    let PatientAge = '';
    if (scheduled_date && birth_date) {
      PatientAge = `${dayjs(scheduled_date).diff(dayjs(birth_date), 'years')}Y`;
    }

    const convertKeys = [
      [REPORT_VARIABLES.ReportContent.code, ReportContent || ''],
      [REPORT_VARIABLES.ReportConclusion.code, ReportConclusion || ''],
      [REPORT_VARIABLES.StudyDateTime.code, studyDateTime || ''],
      [
        REPORT_VARIABLES.ReportDateTime.code,
        updated_at ? dayjs(updated_at).format(getDateFormatted(true)) : '',
      ],
      [REPORT_VARIABLES.Approver.code, approverName || ''],
      [REPORT_VARIABLES.Submitter.code, submitterName || ''],
      [
        REPORT_VARIABLES.InstitutionLogo.code,
        InstitutionLogo
          ? `<img style="max-height: 100px; margin: 0 auto; padding: 2px;" src="${InstitutionLogo}" />`
          : '',
      ],
      [REPORT_VARIABLES.InstitutionName.code, InstitutionName || ''],
      [REPORT_VARIABLES.InstitutionAddress.code, InstitutionAddress || ''],
      [REPORT_VARIABLES.InstitutionPhone.code, InstitutionPhone || ''],
      [REPORT_VARIABLES.InstitutionEmail.code, InstitutionEmail || ''],
      [REPORT_VARIABLES.ProcedureName.code, procedure?.name || ''],
      [REPORT_VARIABLES.OrderDoctorName.code, OrderDoctorName || ''],
      [REPORT_VARIABLES.ReferDoctorName.code, ReferDoctorName || ''],
      [REPORT_VARIABLES.Diagnosis.code, Diagnosis || ''],
      [REPORT_VARIABLES.PatientID.code, PatientID || ''],
      [REPORT_VARIABLES.VisitID.code, VisitID || ''],
      [REPORT_VARIABLES.PatientName.code, PatientName || ''],
      [
        REPORT_VARIABLES.PatientBirthday.code,
        PatientBirthday ? dayjs(PatientBirthday).format(getDateFormatted()) : '',
      ],
      [REPORT_VARIABLES.PatientAge.code, PatientAge ?? ''],
      [REPORT_VARIABLES.PatientSex.code, t(GENDER[PatientSex]) || ''],
      [REPORT_VARIABLES.PatientAddress.code, PatientAddress || ''],
      [REPORT_VARIABLES.PatientPhone.code, convertPhoneNumber(PatientPhone)],
      [REPORT_VARIABLES.PrintDateTime.code, ''],
      [
        REPORT_VARIABLES.Signature.code,
        signature
          ? `<img style="max-height: 100px; margin: 0 auto; padding: 2px;" src=${signature} />`
          : '',
      ],
      [
        REPORT_VARIABLES.QRCode.code,
        qrBase64 ? `<img style="padding: 2px" src="${qrBase64}" />` : '',
      ],
      [
        REPORT_VARIABLES.Password.code,
        PatientPhone ? `${PatientPhone.substring(PatientPhone.length - 4)}a@` : '',
      ],
      [REPORT_VARIABLES.Today.code, dayjs().format(getDateFormatted())],
      [
        REPORT_VARIABLES.PatientBirthdayYear.code,
        PatientBirthday ? dayjs(PatientBirthday).format('YYYY') : '',
      ],
      [
        REPORT_VARIABLES.QRVisit.code,
        QRShareVisit ? `<img style="padding: 2px" src="${QRShareVisit}" />` : '',
      ],
      [REPORT_VARIABLES.QRVisitPassword.code, visit_access_key?.password || ''],
      [
        REPORT_VARIABLES.OrderDate.code,
        OrderDate ? dayjs(OrderDate).format(getDateFormatted(true)) : '',
      ],
      [
        REPORT_VARIABLES.ClinicalInformation.code,
        structureReportData?.CLINICAL_INFORMATION?.render?.() || '',
      ],
      [REPORT_VARIABLES.Comparison.code, structureReportData?.COMPARISON?.render?.() || ''],
      [REPORT_VARIABLES.Technique.code, structureReportData?.TECHNIQUE?.render?.() || ''],
      [REPORT_VARIABLES.Findings.code, structureReportData?.FINDINGS?.render?.() || ''],
      [REPORT_VARIABLES.Impression.code, structureReportData?.IMPRESSION?.render?.() || ''],
      [
        REPORT_VARIABLES.EsignSubmitter.code,
        `<span style='font-size: 0.1pt'>${REPORT_VARIABLES.EsignSubmitter.code}</span>`,
      ],
      [
        REPORT_VARIABLES.EsignApprover.code,
        `<span style='font-size: 0.1pt'>${REPORT_VARIABLES.EsignApprover.code}</span>`,
      ],
      [
        REPORT_VARIABLES.SignatureSubmitter.code,
        imageTag({ src: submitter?.signature, styles: logoStyle }),
      ],
      [
        REPORT_VARIABLES.SignatureApprover.code,
        imageTag({ src: approver?.signature, styles: logoStyle }),
      ],
      [
        REPORT_VARIABLES.SignatureTechnician.code,
        imageTag({ src: technician?.signature, styles: logoStyle }),
      ],
      [REPORT_VARIABLES.Technician.code, technician?.fullname ?? technician?.username ?? ''],
    ];

    return transformData(convertKeys, layoutContent);
  };

  const transformData = (convertKeys = [], data = '') => {
    return convertKeys.reduce((acc, [key, value]) => {
      return acc.replaceAll(key, value);
    }, data);
  };

  const onStart = (_event: DraggableEvent, uiData: DraggableData) => {
    const { clientWidth, clientHeight } = window.document.documentElement;
    const targetRect = draggleRef.current?.getBoundingClientRect();
    if (!targetRect) {
      return;
    }
    setIsResizing(true);
    setBounds({
      left: -targetRect.left + uiData.x,
      right: clientWidth - (targetRect.right - uiData.x),
      top: -targetRect.top + uiData.y,
      bottom: clientHeight - (targetRect.bottom - uiData.y),
    });
  };

  const handleOnDrag = (_event: DraggableEvent, uiData: DraggableData) => {
    setDragPosition({ x: uiData.x, y: uiData.y });
  };

  const handleResizeWindow = useCallback(() => {
    setDisabled(false);
    triggerMouseEvent(draggleRef.current, 'mouseover');
    triggerMouseEvent(draggleRef.current, 'mousedown');
    triggerMouseEvent(document, 'mousemove');
    triggerMouseEvent(draggleRef.current, 'mouseup');
    triggerMouseEvent(draggleRef.current, 'click');
    setDisabled(true);
  }, []);

  useLayoutEffect(() => {
    window.addEventListener('resize', handleResizeWindow);
    return () => window.removeEventListener('resize', handleResizeWindow);
  }, [handleResizeWindow]);

  const triggerMouseEvent = (element, eventType) => {
    const mouseEvent = new Event(eventType, {
      bubbles: true,
      cancelable: true,
    });
    element.dispatchEvent(mouseEvent);
  };

  const orderMenuItems = (reportList || []).map((it) => ({
    key: it?.id,
    label: (
      <div className="flex">
        <div className="flex-1">{it?.order?.procedure?.name || it?.order?.procedure?.code}</div> |{' '}
        <div className="w-32">{formatDateColumn(it?.approved_at)}</div>
      </div>
    ),
    onClick: () => setSelectedReport(it),
  }));

  return (
    <div>
      <Modal
        destroyOnClose={true}
        footer={null}
        mask={false}
        maskClosable={false}
        open={isOpen}
        style={position}
        width={resizeDimension.width}
        zIndex={modalZIndex}
        modalRender={(modal) => (
          <Draggable
            bounds={bounds}
            disabled={disabled}
            nodeRef={draggleRef}
            position={dragPosition}
            onDrag={handleOnDrag}
            onStart={(event, uiData) => onStart(event, uiData)}
            onStop={() => setIsResizing(false)}
          >
            <div ref={draggleRef}>{modal}</div>
          </Draggable>
        )}
        title={
          <div
            style={{
              width: '100%',
              cursor: 'move',
            }}
            onFocus={() => {}}
            onMouseOut={() => {
              setDisabled(true);
            }}
            // fix eslintjsx-a11y/mouse-events-have-key-events
            // https://github.com/jsx-eslint/eslint-plugin-jsx-a11y/blob/master/docs/rules/mouse-events-have-key-events.md
            onMouseOver={() => {
              if (disabled) {
                setDisabled(false);
              }
            }}
            onBlur={() => {}}
            // end
          >
            <div className="select-none">{t('Report')}</div>
          </div>
        }
        wrapClassName={`report-draggable-modal${isActive ? ' is-active-modal' : ''}${
          isResizing ? ' is-resizing' : ''
        }`}
        onCancel={onCancel}
      >
        {!isFetchingReport && orderMenuItems?.length > 0 && (
          <div className="relative flex w-full justify-between rounded-t-sm bg-background-dark-7">
            <Dropdown
              placement="bottomLeft"
              trigger={['click']}
              menu={{
                items: orderMenuItems,
                selectable: true,
                selectedKeys: [selectedReport?.id],
              }}
            >
              <div className="w-full border-t border-secondary-light py-1 px-2 pr-8">
                <CaretDownOutlined className="mr-2 cursor-pointer" />
                {selectedReport?.order?.procedure?.name} |{' '}
                {formatDateColumn(selectedReport?.approved_at)}
              </div>
            </Dropdown>
          </div>
        )}
        <Spin spinning={isFetchingReport} tip={t('Loading...')}>
          <ResizableBox
            height={resizeDimension.height}
            minConstraints={[300, 200]}
            resizeHandles={['se', 'sw']}
            width={resizeDimension.width}
            onResizeStart={() => setIsResizing(true)}
            onResizeStop={() => setIsResizing(false)}
            onResize={(_, { size, handle }) => {
              if (handle === 'sw') {
                const deltaWidth = size.width - resizeDimension.width;
                const newPos = { ...dragPosition };
                newPos.x -= deltaWidth;
                setDragPosition(newPos);
              }
              setResizeDimension({
                width: size?.width || 680,
                height: size?.height || 500,
              });
            }}
          >
            <div className="report-vp-content flex min-h-[120px] flex-col p-2 ">
              {!isFetchingReport &&
                (selectedReport?.pdfURL ? (
                  <PDFViewer style={{ height: '100%' }} url={selectedReport?.pdfURL} />
                ) : selectedReport ? (
                  <div className="text-editor">
                    <div
                      dangerouslySetInnerHTML={{ __html: selectedReport.transformedContent }}
                    ></div>
                  </div>
                ) : (
                  <div className="m-auto m-2 flex flex-col items-center py-[32px]">
                    <div className="mb-2 text-[36px] text-[#6c6c6c]">
                      <FileSearchOutlined />
                    </div>
                    <div>{t("The report isn't ready yet")}</div>
                  </div>
                ))}
            </div>
          </ResizableBox>
        </Spin>
      </Modal>
    </div>
  );
}

ReportModal.propTypes = {
  userAuthenticationService: PropTypes.any.isRequired,
  modalWidth: PropTypes.number,
  isOpen: PropTypes.bool,
  onCancel: PropTypes.func,
  position: PropTypes.object,
  isActive: PropTypes.bool,
  study: PropTypes.any,
};

function imageTag({
  src,
  styles = '',
  replaceValue = '',
}: {
  src: string;
  styles?: string;
  replaceValue?: string;
}) {
  return src ? `<img style="padding: 2px; ${styles}" src=${src} />` : replaceValue;
}
const logoStyle = 'max-height: 100px; margin: 0 auto;';
